import type { ReactNode } from 'react'
import { BbSwitch } from '../../components/BbSwitch'
import type { BooleanSettingKey } from './useSettingsForm'
import { useSettingsForm } from './useSettingsForm'

/* The settings row shape every tab uses: dot, label, optional help text on the
   left; the control on the right. Pulled out of SettingsPage's local `toggle`
   closure when the tabs became separate route components and could no longer
   share one. */

export function SettingsRow({
  htmlFor,
  label,
  help,
  children,
}: {
  /** Set when the control is a labellable element, so the text targets it. */
  htmlFor?: string
  label: string
  help?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="bb-opt">
      <span className="bb-opt-l">
        <span className="bb-opt-dot" />
        <span>
          {htmlFor ? (
            <label className="bb-opt-t" htmlFor={htmlFor}>
              {label}
            </label>
          ) : (
            <span className="bb-opt-t">{label}</span>
          )}
          {help ? <span className="bb-opt-help">{help}</span> : null}
        </span>
      </span>
      <span className="bb-opt-c">{children}</span>
    </div>
  )
}

export function SettingsToggle({
  setting,
  label,
  help,
}: {
  setting: BooleanSettingKey
  label: string
  help?: string
}) {
  const { draft, set } = useSettingsForm()
  return (
    <SettingsRow label={label} help={help}>
      <BbSwitch
        id={setting}
        label={label}
        checked={draft[setting]}
        onChange={(next) => set(setting, next)}
      />
    </SettingsRow>
  )
}

export function SettingsSection({
  title,
  help,
  children,
}: {
  title: string
  help?: string
  children: ReactNode
}) {
  return (
    <div className="bb-sec">
      <h2 className="bb-sec-h">{title}</h2>
      {help ? (
        <p className="bb-map-help" style={{ marginBottom: 16 }}>
          {help}
        </p>
      ) : null}
      {children}
    </div>
  )
}
